import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import { useAdmin } from "./hooks/useAdmin";
import TipTapEditor from "./components/TipTapEditor";
import greekRankingLogo from "./assets/images/logos/greek-ranking-logo-circle-transparent.webp";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import "./AdminRulesPage.css";

const RULES_DOC = doc(db, "admin_rules", "competition_rules");

export default function AdminRulesPage() {
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [activeTab, setActiveTab] = useState("solo");
  const [soloContent, setSoloContent] = useState("");
  const [groupContent, setGroupContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setFetchLoading(true);
      const snap = await getDoc(RULES_DOC);
      if (snap.exists()) {
        const data = snap.data();
        setSoloContent(data.soloContent || "");
        setGroupContent(data.groupContent || "");
      }
    } catch (err) {
      console.error("Error fetching admin rules:", err);
    } finally {
      setFetchLoading(false);
    }
  };

  const saveContent = async () => {
    try {
      setSaving(true);
      await setDoc(RULES_DOC, {
        soloContent,
        groupContent,
        updatedAt: new Date().toISOString(),
      });
      setSaveStatus({ type: "success", message: "Rules saved successfully!" });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error("Error saving admin rules:", err);
      setSaveStatus({ type: "error", message: "Error saving rules." });
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!previewRef.current) return;
    try {
      setPdfGenerating(true);

      const element = previewRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const canvasAspect = canvas.height / canvas.width;
      const scaledHeight = pdfWidth * canvasAspect;

      let yOffset = 0;
      let heightLeft = scaledHeight;

      // First page
      pdf.addImage(imgData, "PNG", 0, yOffset, pdfWidth, scaledHeight);
      heightLeft -= pdfHeight;

      // Additional pages if content is long
      while (heightLeft > 0) {
        yOffset -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, yOffset, pdfWidth, scaledHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save("greek-ranking-competition-rules.pdf");
    } catch (err) {
      console.error("PDF generation error:", err);
      alert("Could not generate PDF. Please try again.");
    } finally {
      setPdfGenerating(false);
    }
  };

  if (adminLoading || fetchLoading) {
    return (
      <div className="arp-loading">
        <div className="arp-spinner" />
        <p>Loading Rules & Regulations...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="arp-denied">
        <h2>🔒 Access Denied</h2>
        <p>You need admin privileges to access this page.</p>
      </div>
    );
  }

  return (
    <div className="arp-wrapper">
      {/* ── Header ── */}
      <div className="arp-header">
        <div className="arp-header-left">
          <img src={greekRankingLogo} alt="Greek Ranking" className="arp-logo" />
          <div>
            <h1>Rules & Regulations</h1>
            <p>Competition organizer rules — saved separately from the homepage</p>
          </div>
        </div>
        <div className="arp-header-actions">
          <button className="arp-preview-btn" onClick={() => setShowPreview(true)}>
            👁️ Preview PDF
          </button>
          <button className="arp-save-btn" onClick={saveContent} disabled={saving}>
            💾 {saving ? "Saving..." : "Save Rules"}
          </button>
        </div>
      </div>

      {saveStatus && (
        <div className={`arp-status ${saveStatus.type}`}>
          {saveStatus.type === "success" ? "✅" : "❌"} {saveStatus.message}
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="arp-tabs">
        <button
          className={`arp-tab ${activeTab === "solo" ? "active" : ""}`}
          onClick={() => setActiveTab("solo")}
        >
          🕺 Solo Rules
        </button>
        <button
          className={`arp-tab ${activeTab === "group" ? "active" : ""}`}
          onClick={() => setActiveTab("group")}
        >
          👥 Group Rules
        </button>
      </div>

      {/* ── Editor ── */}
      <div className="arp-editor-section">
        {activeTab === "solo" && (
          <TipTapEditor
            value={soloContent}
            onChange={setSoloContent}
            placeholder="Enter Solo competition rules for organizers..."
          />
        )}
        {activeTab === "group" && (
          <TipTapEditor
            value={groupContent}
            onChange={setGroupContent}
            placeholder="Enter Group competition rules for organizers..."
          />
        )}
      </div>

      {/* ── Preview Modal ── */}
      {showPreview && (
        <div className="arp-modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="arp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="arp-modal-toolbar">
              <span className="arp-modal-title">PDF Preview</span>
              <div className="arp-modal-actions">
                <button
                  className="arp-download-btn"
                  onClick={handleDownloadPDF}
                  disabled={pdfGenerating}
                >
                  {pdfGenerating ? "⏳ Generating..." : "⬇️ Download PDF"}
                </button>
                <button className="arp-close-btn" onClick={() => setShowPreview(false)}>
                  ✕ Close
                </button>
              </div>
            </div>

            <div className="arp-modal-scroll">
              {/* This div is what gets captured by html2canvas */}
              <div className="arp-pdf-page" ref={previewRef}>
                {/* PDF Header */}
                <div className="arp-pdf-header">
                  <img src={greekRankingLogo} alt="Greek Ranking" className="arp-pdf-logo" crossOrigin="anonymous" />
                  <div className="arp-pdf-title-block">
                    <div className="arp-pdf-title">Greek Ranking</div>
                    <div className="arp-pdf-subtitle">Official Competition Rules & Regulations</div>
                  </div>
                </div>
                <div className="arp-pdf-rainbow-bar" />

                {/* Solo section */}
                <div className="arp-pdf-section">
                  <div className="arp-pdf-section-heading">
                    <span className="arp-pdf-section-icon">🕺</span>
                    Solo Competition Rules
                  </div>
                  <div
                    className="arp-pdf-content"
                    dangerouslySetInnerHTML={{
                      __html: soloContent || "<p><em>No solo rules have been added yet.</em></p>",
                    }}
                  />
                </div>

                <div className="arp-pdf-divider" />

                {/* Group section */}
                <div className="arp-pdf-section">
                  <div className="arp-pdf-section-heading">
                    <span className="arp-pdf-section-icon">👥</span>
                    Group Competition Rules
                  </div>
                  <div
                    className="arp-pdf-content"
                    dangerouslySetInnerHTML={{
                      __html: groupContent || "<p><em>No group rules have been added yet.</em></p>",
                    }}
                  />
                </div>

                {/* PDF Footer */}
                <div className="arp-pdf-footer">
                  <div className="arp-pdf-footer-line" />
                  <p>Greek Ranking — Official Document &nbsp;·&nbsp; {new Date().getFullYear()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
